import { MantineProvider, Menu } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { showOldNotification as showNotification } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import { Flex, IconV3, RpiButton, RpiText } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { AppPage } from "../../../components/AppPage";
import { Pagination } from "../../../components/Pagination";
import { createRpaTableMenuColumn, RpaTableMenu } from "../../../components/RpaTableMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { RpiSearchFilter } from "../../../components/search-filter/RpiSearchFilter";
import { SearchFilterBar } from "../../../components/search-filter/SearchFilterBar";
import { SearchInput, toSearchInputArray } from "../../../configuration/filters/searchInput";
import { dashboardUserColumns } from "../../../configuration/columns/dashboardUser.column";
import { useDashboardUserFilters } from "../../../configuration/filters/dashboardUser.filter";
import { featureFlags } from "../../../featureFlags";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useDrawerRoute } from "../../../hooks/useDrawerRoute";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../../utils/graphql.util";
import { dashboardUserSearchLabels, dashboardUserSearchRenderers } from "../../../utils/searchField.util";
import { DashboardUsersFilter, path as filterPath } from "./DashboardUsersFilter";

export const DashboardUsersView: React.FC = () => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.DashboardUsersQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.DashboardUserSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.DashboardUsersQueryVariables>>();

  const searchFilters = toSearchInputArray(searchInputs);
  const setSearchFilters = (
    recipe: (
      filters: SearchInput<GraphQL.DashboardUserSearchFields>[]
    ) => SearchInput<GraphQL.DashboardUserSearchFields>[]
  ) => {
    setSearchInputs((prev) => recipe(toSearchInputArray(prev)));
  };

  const pagination = useRpiPagination({ perPage: 20 });

  const dashboardUserFilters = useDashboardUserFilters();
  const filterDrawerRoute = useDrawerRoute(filterPath);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const { data, isLoading, error, refetch } = GraphQL.useDashboardUsersQuery(
    {
      limit: pagination.perPage,
      offset: pagination.offset,
      search: {
        inputs: searchInputs,
        sort: sortInput,
        generalInput: searchGeneral,
      },
    },
    {
      onSuccess: (data) => pagination.setTotalCount(data.dashboardUsers.totalCount),
    }
  );
  const dashboardUsers = React.useMemo(() => data?.dashboardUsers.data, [data]);

  React.useEffect(() => {
    if (outlet === null) refetch();
  }, [outlet]);

  const { mutateAsync: deleteUser, ...deleteUserMutation } = GraphQL.useDeleteDashboardUserMutation();

  const modals = useModals();
  const handleDeleteDashboardUser = React.useCallback(
    (dashboardUser: Pick<GraphQL.DashboardUserResponse, "id" | "email">) => {
      if (deleteUserMutation.isLoading) return;

      return modals.openConfirmModal({
        title: "Delete dashboard user",
        centered: true,
        children: <RpiText type="p2" children={`Are you sure you want to delete "${dashboardUser.email}"?`} />,
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: async () => {
          return await deleteUser({ id: dashboardUser.id })
            .then(() => {
              showNotification({
                title: `Deleted ${dashboardUser.email}.`,
                message: "",
                color: "green",
              });
              refetch();
            })
            .catch((error) => {
              showNotification({
                title: "Failed to delete dashboard user.",
                message: error.message,
                color: "red",
              });
            });
        },
      });
    },
    [deleteUser, refetch, deleteUserMutation]
  );

  const canModify = user.is.CUSTOMER_ADMIN || user.is.SUPER_ADMIN;

  return (
    <AppPage path={["Dashboard Users"]} onReload={refetch}>
      {!filterDrawerRoute.isOpen && outlet}

      <AppPage.Section>
        <Flex.Row justify="space-between" align="flex-start">
          {featureFlags.newSearchExperience ? (
            <SearchFilterBar
              placeholder="Search by Username or Email"
              isLoading={isLoading}
              filters={searchFilters}
              onSetFilters={setSearchFilters}
              onSearchSubmit={handleSearchSubmit}
              onFilterClick={filterDrawerRoute.open}
              filterLabels={dashboardUserSearchLabels}
              valueRenderers={dashboardUserSearchRenderers}
            />
          ) : (
            <RpiSearchFilter
              placeholder="Search by Email"
              filters={dashboardUserFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}

          {canModify && (
            <RpiButton
              width="fit"
              variant="brand-outline"
              size="small"
              children="Add new user"
              trailingIcon
              onClick={() => navigateRoute(["dashboard-user:create"])}
            />
          )}
        </Flex.Row>

        <MantineProvider theme={rpaTheme}>
          <RpiTableContainer
            data-testid="dashboard-users-table"
            footer={<Pagination isLoading={isLoading} pagination={pagination} label="Users" />}
          >
            <RpiTable
              emptyMessage={
                searchInputs?.length || searchGeneral
                  ? "No dashboard users found."
                  : "Your dashboard users will appear here."
              }
              isLoading={isLoading}
              error={error}
              perPage={dashboardUsers?.length}
              data={dashboardUsers}
              columns={dashboardUserColumns}
              additionalColumns={
                canModify
                  ? [
                      createRpaTableMenuColumn<Pick<GraphQL.DashboardUserResponse, "id" | "email">>((row) => (
                        <RpaTableMenu>
                          <Menu.Item
                            icon={<IconV3 icon="edit" size={20} />}
                            onClick={() => navigateRoute(["dashboard-user:edit", { id: row.id }])}
                          >
                            Edit
                          </Menu.Item>
                          {user.cognitoUser?.attributes?.email !== row.email && (
                            <Menu.Item
                              icon={<IconV3 icon="delete" size={20} color="inherit" />}
                              onClick={() => handleDeleteDashboardUser({ id: row.id, email: row.email })}
                            >
                              Delete
                            </Menu.Item>
                          )}
                        </RpaTableMenu>
                      )),
                    ]
                  : undefined
              }
              withSortBy={{ onChange: setSortInput }}
            />
          </RpiTableContainer>
        </MantineProvider>
      </AppPage.Section>
      {featureFlags.newSearchExperience && (
        <DashboardUsersFilter
          isOpened={filterDrawerRoute.isOpen}
          onClose={filterDrawerRoute.close}
          filters={searchFilters}
          onApply={setSearchInputs}
        />
      )}
    </AppPage>
  );
};
